// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import AppsUtil from '../../../utils/AppsUtil';
import CompatibilityUtil from '../../../utils/CompatibilityUtil';
import React, {Component} from 'react';
import './CategoryPage.scss';
import BackAndroid from '@react/react-spectrum/Icon/BackAndroid';
import {categoryMap} from '../appPickerConstants';
import {FormattedMessage} from 'react-intl';
import ProductDescCard from '../ProductDescCard/ProductDescCard';
import AppsAnalytics from '../../../analytics/AppsAnalytics';

type Props = {
	getPlatformAppInfo: Function,
	getAppPlatform: Function,
	getAppBtnType: Function,
	history: Object,
	routeParams: Object,
	routeSchema: Object,
	intl: Object,
	environment: string
};

type State = {
	selectedCategory: string
}

export default class CategoryPage extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			selectedCategory: 'combine-images'
		};
	}

	componentDidMount() {
		this.sendAnalytic('render', 'page');
		Object.values(categoryMap).forEach((category: any) => {
			const recommendedApp = category.products[0];
			const sapCode = this.props.getPlatformAppInfo(recommendedApp).sapCode;
			this.sendAnalytic('render', 'intent', category.id, sapCode);
		});
	}

	sendAnalytic(type: string, subType: string, eventValue: ?string, recommendedAppCode?: string) {
		const appsAnalytics = new AppsAnalytics(this.props.environment);
		appsAnalytics.initAnalytics();
		let appConfig = {};
		if (recommendedAppCode) {
			const productConfig = AppsUtil.getFFCProductConfigBySapCode(recommendedAppCode);
			appConfig = {
				sapCode: recommendedAppCode,
				displayName: productConfig.displayName,
				entitled: AppsUtil.isAppEntitled(recommendedAppCode, this.props.getAppPlatform(productConfig))
			};
		}
		switch (type) {
			case 'render':
				appsAnalytics.genericAnalyticEvent(appConfig, type, subType, undefined, undefined, undefined, eventValue, 'Guide Me', 'App Picker');
				break;
			case 'click':
				appsAnalytics.genericAnalyticEvent(appConfig, type, subType, undefined, undefined, undefined, eventValue, 'Guide Me', 'App Picker');
				break;
		}
	}

	setSelectedCategory(selectedCategoryId: string) {
		this.setState({selectedCategory: selectedCategoryId});
	}

	renderCategoryBoxes(category: Object) { // Object.values return Array<any>
		return (<div tabIndex={Object.keys(categoryMap).indexOf(category) + 1} className={`categoryBox${this.state.selectedCategory === category.id ? ' focus-ring' : ''}`}
			onClick={() => {
				this.setSelectedCategory(category.id);
				const recommendedApp = category.products[0];
				const sapCode = this.props.getPlatformAppInfo(recommendedApp).sapCode;
				this.sendAnalytic('click', 'intent', category.id, sapCode);
			}}
			key={`${category.id}-card`}
			onKeyDown={(e) => {e.keyCode === 13 && this.setSelectedCategory(category.id);}}>
			<FormattedMessage id={category.name} />
			<div className={`categoryBoxImage ${category.id}`} >
				<img alt={this.props.intl.formatMessage({id: category.name})} src={category.imgSrc} />
			</div>
		</div>);
	}

	renderCategoryApp() {
		if (this.state.selectedCategory) {
			const selectedCatApps = categoryMap[this.state.selectedCategory].products;
			let supportedApp = selectedCatApps[0];
			if (!AppsUtil.isMobileOrTab()) {
				for (const app in selectedCatApps) {
					const appSapCode = AppsUtil.getTileConfig(AppsUtil.productsData[selectedCatApps[app]]).sapCode;
					if (CompatibilityUtil.isAppSupported(appSapCode) || CompatibilityUtil.isAnyOlderVersionSupported(appSapCode)) {
						supportedApp = selectedCatApps[app];
						break;
					}
				}
			}
			return <ProductDescCard app={supportedApp} interestCategory={this.state.selectedCategory} {...this.props} />;
		}
		return null;
	}

	onBackButtonClick() {
		// route to 'get first app' page
		this.sendAnalytic('click', 'back');
		this.props.history.push(AppsUtil.routeConstructor(this.props.routeSchema.appsPDPRoute, this.props.routeParams.mainAppsTab, this.props.routeParams.product));
	}

	render() {
		return (
			<div className='CategoryPage'>
				<div className='categoryPageHeader'>
					<div className='backBreadCrumb' onClick={() => {this.onBackButtonClick();}}>
						<BackAndroid className='backButton' size='XS'/>
						<FormattedMessage id='apps.appPickerPhotoPlan.categoryPage.back' />
					</div>
					<div className='categoryPageHeading'><FormattedMessage id='apps.appPickerPhotoPlan.categoryPage.heading' /></div>
				</div>
				<div className='categoryBoxes'>
					{Object.keys(categoryMap).map(category => this.renderCategoryBoxes(categoryMap[category]))}
				</div>
				<div className='categoryApp'>
					{this.renderCategoryApp()}
				</div>
			</div>
		);
	}
}